import {useCallback, useEffect, useRef, useState} from 'react';
import {useReportStore} from '../../features/products/useReportStore';

// Shallow compare two arrays of {uid, text} objects
function areFragmentMarksEqual(prev, next) {
    if (prev.length !== next.length) return false;
    for (let i = 0; i < prev.length; i++) {
        if (prev[i].uid !== next[i].uid || prev[i].text !== next[i].text) return false;
    }
    return true;
}

/**
 * Returns [{uid, text}] for every reference mark in the editor.
 * Single source of truth: ProseMirror editor state.
 * Rebuilds on mount, on every doc-changing transaction, and whenever
 * editorView is first set (e.g. after the editor finishes hydrating).
 */
export default function useFragmentMarks() {
    const [fragmentMarks, setFragmentMarks] = useState([]);
    const editorView = useReportStore(s => s.editorView);
    const prevMarksRef = useRef([]);

    const collectFromState = useCallback((state) => {
        const map = new Map(); // uid -> accumulated text
        try {
            const markType = state?.schema?.marks?.reference;
            if (!markType) return [];

            state.doc.descendants((node) => {
                if (!node.isText || !node.marks?.length) return;
                node.marks
                    .filter(m => m.type === markType && m.attrs?.uid)
                    .forEach(m => {
                        const uid = m.attrs.uid;
                        const part = (node.text || '').trim();
                        if (!map.has(uid)) {
                            map.set(uid, part);
                        } else if (part) {
                            const existing = map.get(uid);
                            map.set(uid, existing ? `${existing} ${part}` : part);
                        }
                    });
            });
        } catch (_) { /* ignore */
        }

        return Array.from(map.entries()).map(([uid, text]) => ({
            uid,
            text: text.replace(/\s+/g, ' ').trim(),
        }));
    }, []);

    const apply = useCallback((state) => {
        const next = collectFromState(state);
        if (!areFragmentMarksEqual(prevMarksRef.current, next)) {
            prevMarksRef.current = next;
            setFragmentMarks(next);
        }
    }, [collectFromState]);

    useEffect(() => {
        if (!editorView?.state) {
            if (prevMarksRef.current.length > 0) {
                prevMarksRef.current = [];
                setFragmentMarks([]);
            }
            return;
        }

        // Collect immediately when editorView becomes available
        apply(editorView.state);

        // Patch dispatch to stay in sync with every document change
        const originalDispatch = editorView.dispatch;
        editorView.dispatch = (tr) => {
            originalDispatch.call(editorView, tr);
            if (tr.docChanged) apply(editorView.state);
        };
        return () => {
            editorView.dispatch = originalDispatch;
        };
    }, [editorView, apply]);

    return fragmentMarks;
}
